import { useNotifications } from 'modules/app/notifications';
import { DangerConfirmationModal } from 'src/core/common/components/DangerConfirmationModal';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';

import { type ExpenseCategory } from '../../expenseCategory';
import { useBulkDeleteExpenseCategories } from '../../hooks/useBulkDeleteExpenseCategories';

type Props = {
  expenseCategories: ExpenseCategory[];
  isOpen: boolean;
  onComplete(): void;
  onClose(): void;
};

export const EcBulkDeleteModal = ({
  expenseCategories,
  isOpen,
  onComplete,
  onClose,
}: Props) => {
  const { t } = useTranslation('global');
  const { dangerNotif, successNotif } = useNotifications();
  const bulkDeleteExpenseCategories = useBulkDeleteExpenseCategories();
  const isBudgetsFeatureEnabled = useFeature(FEATURES.BUDGETS);
  const descriptionKey = isBudgetsFeatureEnabled
    ? 'expenseCategories.bulkDelete.modalSubtitleWithBudgetFeature'
    : 'expenseCategories.bulkDelete.modalSubtitleWithoutBudgetFeature';

  const expenseCategoryNames = expenseCategories
    .map((ec) => ec.name)
    .join(', ');

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteExpenseCategories(expenseCategories);

      successNotif(
        t('expenseCategories.bulkDelete.success', {
          count: expenseCategories.length,
        }),
      );
      onComplete();
    } catch (error) {
      dangerNotif(t('expenseCategories.bulkDelete.error'));
      throw error;
    }
  };

  return (
    <DangerConfirmationModal
      isOpen={isOpen}
      title={t('expenseCategories.bulkDelete.modalTitle')}
      description={`${t(descriptionKey, { count: expenseCategories.length })}\n\n${expenseCategoryNames}`}
      actionText={t('expenseCategories.bulkDelete.delete')}
      withRejectionMessage={false}
      onClose={onClose}
      onConfirm={handleBulkDelete}
    />
  );
};
